import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Button, Spinner, Surface } from '@heroui/react';
import type { PublicUserProfile } from '@bunker/shared/types';
import { api } from '@/lib/api';
import { toast } from '@/lib/toast';
import { avatarHue } from '@/lib/avatarHue';

/**
 * Public profile card behind `bunker.cispracs.ru/u/<username>` — the landing
 * page for shared profile links and the mobile QR code. Works WITHOUT a
 * session on purpose: a system-camera QR scan lands here before any login,
 * so the page must render from the public lookup alone.
 */
export function PublicProfilePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { username = '' } = useParams<{ username: string }>();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['publicProfile', username.toLowerCase()],
    queryFn: () => api.getUserByUsername(username),
    enabled: username.length > 0,
    retry: false,
    staleTime: 60_000,
  });

  const user: PublicUserProfile | undefined = data?.user;

  return (
    <div className="relative grid h-full place-items-center overflow-hidden bg-surface p-6">
      {/* Brand glow — one soft radial accent behind the card, so the page
          reads "Bunker" even to someone who has never opened the app. */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-[12%] h-[420px] w-[640px] -translate-x-1/2 rounded-full opacity-25 blur-3xl"
        style={{ background: 'radial-gradient(closest-side, rgb(var(--bk-accent)), transparent 70%)' }}
      />

      {isLoading ? (
        <div className="flex flex-col items-center gap-3 text-muted">
          <Spinner size="lg" />
          <span className="text-sm">{t('ui2.publicProfile.loading')}</span>
        </div>
      ) : isError || !user ? (
        <div className="flex max-w-md flex-col items-center gap-4 text-center">
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">
            {t('ui2.publicProfile.notFoundTitle')}
          </h1>
          <p className="text-sm text-muted">{t('ui2.publicProfile.notFoundHint', { username })}</p>
          <Button variant="secondary" onPress={() => navigate('/')}>
            {t('ui2.publicProfile.open')}
          </Button>
        </div>
      ) : (
        <Surface
          variant="secondary"
          className="relative flex w-full max-w-sm flex-col items-center gap-5 rounded-3xl border border-separator px-8 py-10 text-center shadow-2xl"
        >
          {/* Avatar with a brand gradient ring (the "premium" tell). */}
          <div className="rounded-full bg-gradient-to-br from-[rgb(var(--bk-accent-hover))] to-[rgb(var(--bk-accent))] p-[3px]">
            <div className="rounded-full bg-surface p-[3px]">
              {user.avatarUrl ? (
                <img
                  src={user.avatarUrl}
                  alt=""
                  className="size-28 rounded-full object-cover"
                  draggable={false}
                />
              ) : (
                <div
                  className={`grid size-28 place-items-center rounded-full text-4xl font-semibold ${avatarHue(user.id).bg} ${avatarHue(user.id).text}`}
                >
                  {(user.displayName ?? user.username).slice(0, 1).toUpperCase()}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">
              {user.displayName ?? user.username}
            </h1>
            <div className="text-sm text-accent">
              @{user.username}{' '}
              <span className="text-muted">{t('ui2.publicProfile.inBunker')}</span>
            </div>
          </div>

          {user.bio ? (
            <p className="max-w-xs whitespace-pre-wrap text-sm leading-relaxed text-muted">
              {user.bio}
            </p>
          ) : null}

          <div className="mt-2 flex w-full flex-col gap-2">
            <Button variant="primary" className="w-full" onPress={() => navigate('/')}>
              {t('ui2.publicProfile.open')}
            </Button>
            <Button
              variant="ghost"
              className="w-full"
              onPress={() => {
                void navigator.clipboard
                  .writeText(`${window.location.origin}/u/${user.username}`)
                  .then(() => toast.success(t('ui2.publicProfile.copied')));
              }}
            >
              {t('ui2.publicProfile.copy')}
            </Button>
          </div>
        </Surface>
      )}
    </div>
  );
}
